/**
 * The habit of winning.
 *
 * Imported by BOTH halves -- the API orders the leaderboard and seeds brackets
 * with it, the client shows the number beside each car. One definition, because
 * a leaderboard that disagrees with the seeding it produced is the kind of bug
 * that gets argued about on the floor rather than reported.
 *
 * ── The formula, and why it is this one ──────────────────────────────────────
 *
 *   score = winRate × log10(total + 1) + wins × 2 − losses × 0.5
 *
 * It comes from the Python prototype in docs/prototype and it is kept exactly,
 * because it encodes the thing the app is actually for. Win rate alone crowns a
 * car that won its only race; race count alone crowns whichever car is nearest
 * the sofa. Multiplying the rate by log10 of the races played means a perfect
 * record counts for more the more it survives, and the flat +2 / −0.5 keeps a
 * busy car ahead of an idle one with the same percentage.
 *
 * A car that has never raced scores zero rather than a divide-by-zero, and sits
 * below every car that has -- which is correct: it has not shown anything yet.
 */

export interface CarRecord {
  wins: number
  losses: number
}

export interface Ranked extends CarRecord {
  /** Percentage, 0-100. */
  winRate: number
  /** Two decimals, which is as precise as it is meaningful. */
  score: number
  /** 1-based, after sorting. Ties are broken before this is assigned. */
  rank: number
}

export function winRate({ wins, losses }: CarRecord): number {
  const total = wins + losses
  return total > 0 ? (wins / total) * 100 : 0
}

export function habitScore(record: CarRecord): number {
  const total = record.wins + record.losses
  if (total === 0) return 0
  const raw = winRate(record) * Math.log10(total + 1) + record.wins * 2 - record.losses * 0.5
  return Math.round(raw * 100) / 100
}

/**
 * Sort, then number.
 *
 * The tie-breaks are score, then win rate, then wins -- the prototype's order.
 * Name is the last one, added here: without it two cars with identical records
 * swap places between two requests that are otherwise the same, and a
 * leaderboard that reorders itself while nothing happened looks broken.
 */
export function rank<T extends CarRecord & { name: string }>(cars: readonly T[]): (T & Ranked)[] {
  return [...cars]
    .map((car) => ({ ...car, winRate: winRate(car), score: habitScore(car) }))
    .sort((a, b) =>
      b.score - a.score
      || b.winRate - a.winRate
      || b.wins - a.wins
      || a.name.localeCompare(b.name))
    .map((car, i) => ({ ...car, rank: i + 1 }))
}

/**
 * First-round pairings for a bracket: 1 v N, 2 v N-1, and so on.
 *
 * Seeded rather than drawn at random, for the reason every tournament is: the
 * two strongest cars should not be able to meet in the first round. `seeds` is
 * the ranked list, best first; the returned pairs carry the seed NUMBER as well
 * as the entry, because the bracket is read as "1 versus 8" all evening.
 */
export function seedPairs<T>(seeds: readonly T[]): { a: T; b: T; seedA: number; seedB: number }[] {
  const pairs: { a: T; b: T; seedA: number; seedB: number }[] = []
  for (let i = 0; i < seeds.length / 2; i++) {
    const j = seeds.length - 1 - i
    pairs.push({ a: seeds[i] as T, b: seeds[j] as T, seedA: i + 1, seedB: j + 1 })
  }
  return pairs
}

/** Where the winner of (round, position) goes, and which side it lands on. */
export function nextSlot(round: number, position: number): { round: number; position: number; side: 'a' | 'b' } {
  return { round: round + 1, position: Math.ceil(position / 2), side: position % 2 === 1 ? 'a' : 'b' }
}

/** 4 cars is two rounds, 8 is three. */
export function roundsFor(size: number): number {
  return Math.log2(size)
}

/** The name of a round, counted from the end: the last one is always the final. */
export function roundName(round: number, rounds: number): string {
  const fromEnd = rounds - round
  if (fromEnd === 0) return 'Final'
  if (fromEnd === 1) return 'Semi-finals'
  if (fromEnd === 2) return 'Quarter-finals'
  return `Round ${round}`
}
